/**
 * Frontend Error Handler
 * Simple error tracking utility for frontend scripts
 *
 * Backward compatible with:
 * - logError(message, error, metadata)
 * - handleError(message, error, metadata)
 * - handleFatalError(message, error, metadata)
 * - logSuccess(message, metadata)
 * - logInfo(message, metadata)
 */

export default class ErrorHandler {
  /**
   * @param {Object} options
   * @param {string} [options.module='Unknown'] Module name shown in logs/records
   * @param {number} [options.maxErrors=25] Back-compat: caps stored errors AND successes (see maxRecords)
   * @param {number} [options.maxRecords] Preferred: caps stored errors AND successes
   * @param {boolean} [options.debug=false] If true, enables info logs
   * @param {(record: Object) => void} [options.onRecord] Optional hook called whenever a record is stored
   * @param {(metadata: any) => any} [options.sanitizeMetadata] Optional custom metadata sanitizer
   */
  constructor(options = {}) {
    this.module = options.module || 'Unknown';

    // Backward compatible option name, but maxRecords is preferred.
    const max = options.maxRecords ?? options.maxErrors ?? 25;
    this.maxRecords = Number.isFinite(max) ? max : 25;

    this.debug = !!options.debug;

    this.errors = [];
    this.successes = [];

    this.onRecord = typeof options.onRecord === 'function' ? options.onRecord : null;
    this.sanitizeMetadata =
      typeof options.sanitizeMetadata === 'function' ? options.sanitizeMetadata : this._defaultSanitizeMetadata;
  }

  /**
   * Track a successful operation
   */
  logSuccess(message, metadata = {}) {
    const record = this._makeRecord({
      kind: 'success',
      severity: 'info',
      message,
      error: null,
      metadata
    });

    this.successes.push(record);
    this._trim(this.successes);
    this._emit(record);
  }

  /**
   * Track an error
   *
   * @param {string} message
   * @param {any} error
   * @param {Object} metadata
   * @param {Object} [options]
   * @param {'warn'|'error'|'fatal'} [options.severity='error']
   */
  logError(message, error, metadata = {}, options = {}) {
    const severity = options?.severity || 'error';

    const record = this._makeRecord({
      kind: 'error',
      severity,
      message,
      error,
      metadata
    });

    this.errors.push(record);
    this._trim(this.errors);
    this._emit(record);

    // Console behavior
    if (severity === 'warn') {
      console.warn(`[${this.module}] ${message}`, error, record.metadata);
    } else if (severity === 'fatal') {
      console.error(`[${this.module}] [FATAL] ${message}`, error, record.metadata);
    } else {
      console.error(`[${this.module}] ${message}`, error, record.metadata);
    }

    return record;
  }

  /**
   * Handle a fatal error (backward compatible)
   */
  handleFatalError(message, error, metadata = {}) {
    // One log path only (avoids duplicate console output)
    return this.logError(message, error, metadata, { severity: 'fatal' });
  }

  /**
   * Handle an error (alias for logError for backward compatibility)
   */
  handleError(message, error, metadata = {}) {
    return this.logError(message, error, metadata);
  }

  /**
   * Log info message (for backward compatibility with CalendarLogger interface)
   */
  logInfo(message, metadata = {}) {
    if (!this.debug) return;

    const record = this._makeRecord({
      kind: 'info',
      severity: 'info',
      message,
      error: null,
      metadata
    });

    // We don't store info records by default to avoid noise,
    // but we can emit it to the hook if present.
    this._emit(record);

    console.log(`[${this.module}] ${message}`, record.metadata);
  }

  /**
   * Get recent errors
   */
  getRecentErrors(limit = 5) {
    return this.errors.slice(-limit);
  }

  /**
   * Check if there are critical issues
   * - by default, counts only severity 'error' or 'fatal'
   *
   * @param {Object} [options]
   * @param {number} [options.withinMs] Only consider errors within the last N ms
   * @param {('warn'|'error'|'fatal')[]} [options.severities]
   */
  hasCriticalIssues(options = {}) {
    const severities = options.severities || ['error', 'fatal'];
    const now = Date.now();
    const withinMs = Number.isFinite(options.withinMs) ? options.withinMs : null;

    return this.errors.some((r) => {
      if (!severities.includes(r.severity)) return false;
      if (withinMs == null) return true;

      const ts = Date.parse(r.timestamp);
      if (Number.isNaN(ts)) return true; // if timestamp can't parse, treat as present
      return now - ts <= withinMs;
    });
  }

  /**
   * Clear error history (kept for backward compatibility)
   */
  clearErrorHistory() {
    this.errors = [];
  }

  /**
   * Clear all history (new helper)
   */
  clearHistory() {
    this.errors = [];
    this.successes = [];
  }

  /**
   * Get error count
   */
  getErrorCount() {
    return this.errors.length;
  }

  /**
   * Export current state in a JSON-safe structure
   */
  toJSON() {
    return this.getState();
  }

  /**
   * Get current state
   */
  getState() {
    return {
      module: this.module,
      maxRecords: this.maxRecords,
      errorCount: this.errors.length,
      successCount: this.successes.length,
      errors: [...this.errors],
      successes: [...this.successes]
    };
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    const fatalErrors = this.errors.filter(e => e.severity === 'fatal').length;
    const recentCutoff = Date.now() - (5 * 60 * 1000); // 5 minutes
    const recentErrors = this.errors.filter(e => {
      const ts = Date.parse(e.timestamp);
      return !Number.isNaN(ts) && ts > recentCutoff;
    }).length;

    // Group errors by category
    const errorsByCategory = {};
    for (const error of this.errors) {
      const category = error.metadata?.category || 'uncategorized';
      errorsByCategory[category] = (errorsByCategory[category] || 0) + 1;
    }

    return {
      totalErrors: this.errors.length,
      fatalErrors,
      recentErrors,
      errorsByCategory
    };
  }

  // -----------------------
  // Internal helpers
  // -----------------------

  _trim(arr) {
    while (arr.length > this.maxRecords) arr.shift();
  }

  _emit(record) {
    if (this.onRecord) {
      try {
        this.onRecord(record);
      } catch (e) {
        // don't let hook errors break the app
        console.warn(`[${this.module}] ErrorHandler onRecord hook failed`, e);
      }
    }
  }

  _makeRecord({ kind, severity, message, error, metadata }) {
    const isError = error instanceof Error;

    return {
      timestamp: new Date().toISOString(),
      module: this.module,
      kind, // 'error' | 'success' | 'info'
      severity, // 'info' | 'warn' | 'error' | 'fatal'
      message: String(message || ''),
      error: error == null ? null : (isError ? error.message : String(error)),
      stack: isError ? error.stack : '',
      metadata: this.sanitizeMetadata(metadata)
    };
  }

  /**
   * Default metadata sanitizer:
   * - tries to return something JSON-safe and small-ish
   * - avoids throwing on circular refs
   */
  _defaultSanitizeMetadata(metadata) {
    if (metadata == null) return {};

    // If it's already a primitive, wrap it
    const t = typeof metadata;
    if (t === 'string' || t === 'number' || t === 'boolean') return { value: metadata };

    // If it's an Error, convert
    if (metadata instanceof Error) {
      return { error: metadata.message, stack: metadata.stack || '' };
    }

    // Try safe JSON serialization
    try {
      const seen = new WeakSet();
      const json = JSON.stringify(metadata, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);

          // Strip common heavy objects
          if (value instanceof File) return `File(${value.name}, ${value.size} bytes)`;
          if (value instanceof Blob) return `Blob(${value.size} bytes)`;

          // Avoid DOM nodes
          if (typeof Node !== 'undefined' && value instanceof Node) return '[DOM Node]';
        }
        return value;
      });

      // Parse back into object to ensure it's JSON-safe
      return JSON.parse(json);
    } catch {
      // Last resort: string form
      return { value: String(metadata) };
    }
  }
}
